import { Link, useParams } from "react-router-dom";
import { useState, useMemo } from "react";
import { useDispatch } from "react-redux";
import { addToCart } from "../../redux/cartSlice";
import { hasVariants, getPriceRange } from "../../utils/variants";
import { useCurrency, formatPrice } from "../../utils/currency";
import WhatsAppEnquiryButton from "../../common/whatsappEnquiryButton";
import { useGetProductsByCat, useGetCategoryById } from "../../backend/hooks";
import { toast, ToastContainer } from 'react-toastify';
import WhatsAppButton from '../../common/whatsapp';
import { Skeleton, HeroTextSkeleton } from '../../common/skeleton';

export default function ProductList() {
  const { id } = useParams();
  const dispatch = useDispatch();
  const { currency, isPakistan } = useCurrency();

  // Fetch category details
  const { data: categoryData, isLoading: categoryLoading, error: categoryError } = useGetCategoryById(id);
  // Fetch products for this category
  const { data, isLoading: productsLoading, error: productsError } = useGetProductsByCat(id);

  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("featured");

  const error = categoryError || productsError;

  const filteredProducts = useMemo(() => {
    if (!data) return [];
    let products = [...data];

    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      products = products.filter(
        (p) =>
          p.title?.toLowerCase().includes(term) ||
          p.description?.toLowerCase().includes(term) ||
          p.tags?.join(" ").toLowerCase().includes(term)
      );
    }

    // Sort on the price each card shows, which for a product with options is
    // its cheapest in-stock option rather than the base price.
    const sortPrice = (product) => {
      const range = getPriceRange(product, currency);
      return range.available ? range.min : Number.POSITIVE_INFINITY;
    };

    switch (sortBy) {
      case "price_asc":
        products.sort((a, b) => sortPrice(a) - sortPrice(b));
        break;
      case "price_desc":
        products.sort((a, b) => sortPrice(b) - sortPrice(a));
        break;
      default:
        break;
    }
    return products;
    // currency matters: the geo lookup resolves after the first render and can
    // flip it, and price sorting is done on the figure each card actually shows
  }, [data, searchTerm, sortBy, currency]);

  // Use category data for hero
  const categoryName = categoryData?.name || "Products";
  const categoryDescription = categoryData?.description ||
    "Explore our curated collection – designed to support therapy, rehabilitation, and everyday wellness.";

  const getImageUrl = (product) => {
    if (product.images && product.images.length > 0) {
      return process.env.REACT_APP_BE_URL + product.images[0];
    }
    return "https://via.placeholder.com/400x500?text=No+Image";
  };

  const renderRating = (product) => {
    const rating = product.rating || 5;
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;
    const stars = [];

    for (let i = 0; i < fullStars; i++) {
      stars.push(
        <span
          key={i}
          className="material-symbols-outlined text-tertiary text-sm"
          style={{ fontVariationSettings: "'FILL' 1" }}
        >
          star
        </span>
      );
    }
    if (hasHalfStar) {
      stars.push(
        <span
          key="half"
          className="material-symbols-outlined text-tertiary text-sm"
          style={{ fontVariationSettings: "'FILL' 0.5" }}
        >
          star_half
        </span>
      );
    }
    for (let i = stars.length; i < 5; i++) {
      stars.push(
        <span
          key={i}
          className="material-symbols-outlined text-outline-variant text-sm"
          style={{ fontVariationSettings: "'FILL' 1" }}
        >
          star
        </span>
      );
    }
    return (
      <div className="flex items-center gap-1 mb-3">
        {stars}
        <span className="text-xs text-on-surface-variant ml-1 font-medium">
          ({product.reviewCount || 0})
        </span>
      </div>
    );
  };

  // Skeleton card – same height structure
  const SkeletonCard = () => (
    <div className="rounded-xl bg-surface-container-lowest overflow-hidden flex flex-col h-full">
      <Skeleton className="h-64 md:h-72 w-full" />
      <div className="p-6 flex-1 flex flex-col">
        <Skeleton className="h-3 w-24 rounded mb-3" />
        <Skeleton className="h-5 w-3/4 rounded mb-3" />
        <Skeleton className="h-3 w-full rounded mb-2" />
        <Skeleton className="h-3 w-2/3 rounded mb-4" />
        <Skeleton className="h-6 w-1/3 rounded mb-5" />
        <Skeleton className="h-12 w-full rounded-xl mt-auto" />
      </div>
    </div>
  );

  // Handle Add to Cart – prevent navigation. Products that offer options
  // cannot be added from a card, since there is no price until one is picked;
  // the card falls through to the detail page instead.
  const handleAddToCart = (e, product) => {
    if (hasVariants(product)) return;
    e.preventDefault();
    e.stopPropagation();
    dispatch(addToCart(product));
  };

  if (error) {
    return (
      <main className="pt-32 pb-24 px-6 max-w-7xl mx-auto text-center">
        <div className="text-lg text-error">Error: {error.message}</div>
      </main>
    );
  }

  return (
    <main className="pt-24">
      <ToastContainer />
      <WhatsAppButton />
      {/* Hero Section */}
      <section className="w-full bg-[#15415c] py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-6 text-center">
          {categoryLoading ? (
            <HeroTextSkeleton />
          ) : (
            <>
              <h1 className="text-3xl md:text-5xl font-extrabold text-white tracking-tight">
                {categoryName}
              </h1>
              <p className="mt-3 text-lg text-white/80 max-w-2xl mx-auto">
                {categoryDescription}
              </p>
            </>
          )}
        </div>
      </section>

      {/* Products Grid Container */}
      <div className="pt-12 pb-24 px-6 max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          {productsLoading ? (
            <Skeleton className="h-4 w-36 rounded" />
          ) : (
            <p className="text-sm text-gray-500">
              {`${filteredProducts.length} ${filteredProducts.length === 1 ? "product" : "products"} available`}
            </p>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-x-8 gap-y-12">
          {productsLoading
            ? Array.from({ length: 8 }).map((_, idx) => <SkeletonCard key={idx} />)
            : filteredProducts.map((product, index) => (
              <Link
                key={product.id}
                to={`/ProductDetail/${product.id}`}
                className="group block relative overflow-hidden rounded-xl bg-surface-container-lowest transition-all duration-500 hover:shadow-2xl hover:shadow-primary/5 h-full flex flex-col"
              >
                <div className="h-64 md:h-72 bg-surface-container-high overflow-hidden">
                  <img
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    alt={product.title}
                    src={getImageUrl(product)}
                  />
                </div>
                <div className="p-6 flex-1 flex flex-col">
                  {renderRating(product)}
                  <h3 className="text-lg font-bold text-on-surface mb-1 tracking-tight group-hover:text-primary transition-colors line-clamp-2">
                    {product.title}
                  </h3>
                  <p className="text-on-surface-variant text-sm mb-3 line-clamp-2 flex-1">
                    {product.description?.length > 80
                      ? product.description.substring(0, 80) + '...'
                      : product.description}
                  </p>
                  <p className="text-primary font-extrabold text-xl mb-5">
                    {getPriceRange(product, currency).from && (
                      <span className="text-xs font-bold uppercase tracking-widest text-on-surface-variant mr-1">
                        From
                      </span>
                    )}
                    {formatPrice(getPriceRange(product, currency))}
                  </p>
                  {isPakistan ? (
                    <button
                      className="w-full py-3 px-6 bg-primary text-on-primary rounded-xl font-bold flex items-center justify-center gap-2 transition-all hover:bg-primary-container active:scale-95 mt-auto"
                      onClick={(e) => handleAddToCart(e, product)}
                    >
                      <span className="material-symbols-outlined text-xl">
                        {hasVariants(product) ? "tune" : "add_shopping_cart"}
                      </span>
                      {hasVariants(product) ? "Choose an Option" : "Add to Cart"}
                    </button>
                  ) : (
                    <WhatsAppEnquiryButton
                      product={product}
                      currency={currency}
                      asButton
                      className="w-full py-3 px-6 rounded-xl mt-auto"
                    />
                  )}
                </div>
              </Link>
            ))}
        </div>

        {!productsLoading && filteredProducts.length === 0 && (
          <div className="text-center py-12 text-on-surface-variant">
            No products found.
          </div>
        )}
      </div>
    </main>
  );
}